import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  PurchaseSubscriptionContextProvider,
  usePurchaseSubscriptionContext,
} from '../../../PurchaseSubscription/context/PurchaseSubscriptionContext';
import { useMediaQuery } from '@mantine/hooks';
import { Button, Drawer, Group, Loader, Modal, Paper, Radio, Stack, Text } from '@mantine/core';
import { SelectablePlanRadioOptions } from '../components/SelectablePlanRadioOptions';
import { useDispatch, useSelector } from 'react-redux';
import { cartActions } from '../../../../reducers/subscriptionCart';
import { expectUnreachable, formattedCentsToDollars, isCreditCardExpired, mkError } from '../../../../lib/util';
import { CartPricingProvider, useCartPricing } from '../../../../contexts/CartPricingContext';
import useHandlePurchase from '../../../../hooks/useHandlePurchase';
import { gqlTypes, AppState } from '../../../../types';
import { billingInfoQuery } from '../../../../graphql-operations';
import { ApolloQueryResult, useQuery } from '@apollo/client';
import { CartModeProvider, CartModes, useSubscriptionCartMode } from '../../../../lib/cartModes';
import { priceCartForCheckout } from '../../../../lib/pricing';
import { CheckoutContextProvider } from '../../../../lib/CheckoutContext';
import { MarketingOptInProvider } from '../../../../contexts/MarketingOptInContext';
import { logInternalError } from '../../../../lib/errors';
import { notifications } from '@mantine/notifications';
import { DateTime } from 'luxon';
import CreditCardIcon from '../../../../assets/images/icons/icon_credit_card_outline.svg';
import { Image } from '../../../../components/Image';
import { webSubscriptionManage as events } from '../../../../lib/analytics/events';
import { useLocale } from '../../../../contexts/LocaleContext';

interface AddSubscriptionModalProps {
  opened: boolean;
  onClose: () => void;
  moduleId?: string;
  refetchAllSubscriptions: () => Promise<ApolloQueryResult<gqlTypes.ECOMMERCE_allSubscriptionsForCurrentUser>>;
}

enum ModalPages {
  SELECT,
  CONFIRM,
}

function SelectMembershipPage({
  setSelectedSubscriptionSku,
  selectedSubscriptionSku,
  device,
  setPage,
}: {
  setSelectedSubscriptionSku: React.Dispatch<React.SetStateAction<string>>;
  selectedSubscriptionSku: string;
  device: gqlTypes.purchaseSubscriptionDeviceDetails;
  setPage: React.Dispatch<React.SetStateAction<ModalPages>>;
}) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { currency } = useLocale();
  const handleSelectMembership = (value: string) => {
    setSelectedSubscriptionSku(value);
  };
  const handleNext = () => {
    dispatch(
      cartActions.setCartItem({
        moduleId: device.moduleId,
        sku: selectedSubscriptionSku,
        currency,
      }),
    );
    events.overview.addSubscriptionPickedPlan({ petId: device.pet?.id, newSku: selectedSubscriptionSku });
    setPage(ModalPages.CONFIRM);
  };

  return (
    <Stack>
      <Text fz={24} fw={600}>
        {t('add_a_membership')}
      </Text>
      <Radio.Group value={selectedSubscriptionSku} onChange={handleSelectMembership}>
        <SelectablePlanRadioOptions
          hasSubscriptionOverride={device.hasSubscriptionOverride}
          purchasableSubscriptionOptions={device.purchasableSubscriptionOptions}
        />
      </Radio.Group>
      <Button color="dark" w="100%" radius="xl" onClick={handleNext} disabled={selectedSubscriptionSku === ''}>
        {t('next')}
      </Button>
    </Stack>
  );
}

function ConfirmMembershipPage({
  selectedSubscriptionOption,
  onClose,
  petId,
  refetchAllSubscriptions,
}: {
  selectedSubscriptionOption?: gqlTypes.subscriptionOption;
  onClose: () => void;
  petId?: string;
  refetchAllSubscriptions: () => Promise<ApolloQueryResult<gqlTypes.ECOMMERCE_allSubscriptionsForCurrentUser>>;
}) {
  const cartPricing = useCartPricing();

  const emailAddress = useSelector((state: AppState) => state.session?.email);

  const {
    loading: billingQueryLoading,
    error: billingQueryError,
    data: billingQueryData,
  } = useQuery<gqlTypes.ECOMMERCE_billingInfoFromBillingAccount>(billingInfoQuery, {
    onError(err) {
      logInternalError(err);
    },
  });
  const billingInfo = billingQueryData?.currentUser.billingAccount?.billingInfo;
  const editBillingInfoUrl = billingQueryData?.currentUser.editBillingInfoUrl;
  const cardExpired =
    billingInfo?.paymentInfo.__typename === 'ObfuscatedCardInfo'
      ? isCreditCardExpired(billingInfo.paymentInfo.month, billingInfo.paymentInfo.year)
      : false;

  const DATE_FORMAT_TOKEN = 'MMMM d, y';
  const renewalDateString = selectedSubscriptionOption
    ? DateTime.utc().plus({ months: selectedSubscriptionOption.renewalMonths }).toFormat(DATE_FORMAT_TOKEN)
    : '';

  const { t } = useTranslation();
  const { handlePurchase, purchaseMutationLoading } = useHandlePurchase({
    cartPricing,
    onError: (err) => {
      logInternalError(err);
      notifications.show({
        title: t('something_went_wrong'),
        message: t('please_try_again_later_or_contact_support'),
        color: 'red',
        autoClose: false,
      });
    },
    onSuccess: () => {
      refetchAllSubscriptions();
      notifications.show({
        message: t('your_membership_has_been_reactivated'),
        color: 'green',
      });
      events.overview.addSubscriptionCompleted({ petId: petId, newSku: selectedSubscriptionOption?.sku });
      onClose();
    },
  });

  const handlePurchaseSubscription = () => {
    events.overview.addSubscriptionConfirmed({ petId: petId, newSku: selectedSubscriptionOption?.sku });
    handlePurchase(emailAddress);
  };
  return (
    <Stack>
      <Text fz={24} fw={600}>
        {t('confirm')}
      </Text>
      <Stack align="center" spacing={24}>
        <Stack spacing={4} w={'100%'}>
          <Paper radius={8} bg={'#F3F3F2'}>
            <Stack p={12} spacing={4}>
              <Group position="apart">
                <Text fz={14} fw={700}>
                  {selectedSubscriptionOption?.name}
                </Text>
                <Text fz={14} fw={700}>
                  {formattedCentsToDollars(selectedSubscriptionOption?.priceInCents ?? 0)}
                </Text>
              </Group>
              {cartPricing.activationFeeInCents !== undefined && (
                <Group position="apart">
                  <Text fz={14} fw={700}>
                    {t('activation_fee')}
                  </Text>
                  <Text fz={14} fw={700}>
                    {formattedCentsToDollars(cartPricing.activationFeeInCents)}
                  </Text>
                </Group>
              )}
              <Group position="apart">
                <Text fz={14} fw={700}>
                  {t('tax')}
                </Text>
                <Text fz={14} fw={700}>
                  {formattedCentsToDollars(cartPricing.taxInCents ?? 0)}
                </Text>
              </Group>
              <Group position="apart">
                <Text fz={16} fw={600}>
                  {t('due_today')}
                </Text>
                <Text fz={16} fw={600}>
                  {formattedCentsToDollars(cartPricing.totalInCents)}
                </Text>
              </Group>
            </Stack>
          </Paper>
          <Paper radius={8} bg={'#F3F3F2'}>
            <Group position="apart" p={12}>
              <Group spacing={4}>
                <Image image={{ type: 'image', url: CreditCardIcon }} />
                {billingQueryLoading && <Loader variant="dots" color="dark" />}
                {billingInfo?.paymentInfo.__typename === 'ObfuscatedCardInfo' && (
                  <Text fz={14} fw={700} color={cardExpired ? 'red' : 'dark'}>
                    {billingInfo.paymentInfo.type}
                    {' • • • • '}
                    {billingInfo.paymentInfo.lastFour}
                  </Text>
                )}
                {billingInfo?.paymentInfo.__typename === 'PayPalInfo' && (
                  <Text fz={14} fw={700}>
                    {t('paypal')}
                  </Text>
                )}
                {!billingQueryLoading && !billingQueryError && !cardExpired && (
                  <Text fz={12} fw={500} color="type-light-secondary">
                    {t('currently_on_file')}
                  </Text>
                )}
                {cardExpired && (
                  <Text fz={12} fw={500} color="red">
                    {t('expired')}
                  </Text>
                )}
              </Group>

              {cardExpired && editBillingInfoUrl !== null && editBillingInfoUrl !== undefined && (
                <Button component="a" href={editBillingInfoUrl} target="_blank" variant="subtle" color="dark" px={4}>
                  <Text td="underline" fz={14} fw={700}>
                    {t('update_card_info')}
                  </Text>
                </Button>
              )}
            </Group>
          </Paper>
        </Stack>
        <Text color="type-light-secondary" fz={12} fw={500}>
          {t('your_membership_will_renew_on_date', { date: renewalDateString })}
        </Text>
        <Button
          color="dark"
          radius="xl"
          onClick={handlePurchaseSubscription}
          loading={purchaseMutationLoading}
          w={'100%'}
          disabled={cardExpired}
        >
          {t('purchase_membership')}
        </Button>
      </Stack>
    </Stack>
  );
}

function AddSubscriptionModalContent(props: AddSubscriptionModalProps) {
  const { device } = usePurchaseSubscriptionContext();
  const [selectedSubscriptionSku, setSelectedSubscriptionSku] = useState<string>('');
  const [page, setPage] = useState<ModalPages>(ModalPages.SELECT);

  const selectedSubscriptionOption = useMemo(() => {
    return device.purchasableSubscriptionOptions.find((option) => option.sku === selectedSubscriptionSku);
  }, [device.purchasableSubscriptionOptions, selectedSubscriptionSku]);

  switch (page) {
    case ModalPages.SELECT:
      return (
        <SelectMembershipPage
          setSelectedSubscriptionSku={setSelectedSubscriptionSku}
          selectedSubscriptionSku={selectedSubscriptionSku}
          device={device}
          setPage={setPage}
        />
      );
    case ModalPages.CONFIRM:
      return (
        <ConfirmMembershipPage
          selectedSubscriptionOption={selectedSubscriptionOption}
          onClose={props.onClose}
          petId={device.pet?.id}
          refetchAllSubscriptions={props.refetchAllSubscriptions}
        />
      );
    default:
      expectUnreachable(page);
      return mkError('Something went wrong');
  }
}

function AddSubscriptionModalContainer(props: AddSubscriptionModalProps) {
  const { opened, onClose } = props;
  const largeScreen = useMediaQuery('(min-width: 48em)');

  return largeScreen ? (
    <Modal
      opened={opened}
      onClose={onClose}
      closeButtonProps={{ iconSize: 24, size: 30, color: 'dark', radius: 'xl' }}
      centered={largeScreen}
      radius={16}
      px={20}
      pb={40}
    >
      <AddSubscriptionModalContent {...props} />
    </Modal>
  ) : (
    <Drawer
      opened={opened}
      onClose={onClose}
      position="bottom"
      closeButtonProps={{ iconSize: 24, size: 30, color: 'dark' }}
      styles={() => ({
        inner: {
          '.mantine-Drawer-content': {
            borderTopLeftRadius: 16,
            borderTopRightRadius: 16,
          },
        },
        close: {
          height: 32,
          width: 32,
        },
      })}
      size={680}
    >
      <AddSubscriptionModalContent {...props} />
    </Drawer>
  );
}

/** This provider is added so that we can refetch the cart within the new cart mode we're setting for this component */
function PricingProvider({ children }: { children: React.ReactNode }) {
  const { cartSelector, checkoutType } = useSubscriptionCartMode();
  const cart = useSelector(cartSelector);
  const fetchPricing = useCallback(() => priceCartForCheckout(cart, { checkoutType }), [cart, checkoutType]);
  return <CartPricingProvider fetchPricing={fetchPricing}>{children}</CartPricingProvider>;
}

export function AddSubscriptionModal(props: AddSubscriptionModalProps) {
  return (
    <PurchaseSubscriptionContextProvider moduleId={props.moduleId}>
      <CartModeProvider mode={CartModes.purchaseSubscription}>
        <MarketingOptInProvider>
          <CheckoutContextProvider>
            <PricingProvider>
              <AddSubscriptionModalContainer {...props} />
            </PricingProvider>
          </CheckoutContextProvider>
        </MarketingOptInProvider>
      </CartModeProvider>
    </PurchaseSubscriptionContextProvider>
  );
}
